import { Link } from "react-router-dom";
import { BarChart3, ArrowLeft } from "lucide-react";

function PolicyLayout({ title, children }) {
    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            <header className="glass-header sticky top-0 z-50 border-b border-white/10">
                <div className="max-w-4xl mx-auto flex items-center justify-between h-14 px-6">
                    <Link to="/" className="flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-[#007AFF]" />
                        <span className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </Link>
                    <Link to="/" className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 transition-colors">
                        <ArrowLeft className="w-3 h-3" /> Back
                    </Link>
                </div>
            </header>
            <div className="max-w-3xl mx-auto px-6 py-16">
                <h1 className="text-3xl font-bold tracking-tight text-white mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>{title}</h1>
                <div className="prose prose-invert prose-sm max-w-none space-y-6 text-zinc-300 leading-relaxed text-sm">
                    {children}
                </div>
                <div className="border-t border-white/10 mt-16 pt-6">
                    <p className="text-xs text-zinc-600">&copy; 2026 TradeNalysis. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
}

export function TermsPage() {
    return (
        <PolicyLayout title="Terms of Service">
            <p><strong>Effective Date:</strong> January 1, 2026</p>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>1. Acceptance of Terms</h2>
                <p>By accessing or using TradeNalysis ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>2. Description of Service</h2>
                <p>TradeNalysis is an AI-powered trading technical analysis platform that provides entry points, stop loss levels, and take profit targets for educational and informational purposes only. The Service includes real-time market data, candlestick charts, and AI-generated analysis across stocks, forex, crypto, and futures markets.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>3. Not Financial Advice</h2>
                <p>The analysis, signals, and information provided by TradeNalysis are for <strong>educational and informational purposes only</strong> and do not constitute financial advice, investment advice, or trading recommendations. You should consult with a qualified financial advisor before making any investment decisions. Trading involves substantial risk of loss and is not suitable for all investors.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>4. Account Registration</h2>
                <p>You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>5. Subscription and Payment</h2>
                <p>TradeNalysis offers a 3-day free trial followed by a monthly subscription of $19.00 USD. Payment is processed securely through Stripe. By subscribing, you authorize recurring monthly charges until you cancel your subscription.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>6. No Refund Policy</h2>
                <p><strong>All sales are final. TradeNalysis does not offer refunds for any subscription payments.</strong> You may cancel your subscription at any time, and you will retain access until the end of your current billing period. It is your responsibility to evaluate the Service during the free trial period before subscribing.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>7. Limitation of Liability</h2>
                <p>TradeNalysis shall not be liable for any financial losses, damages, or claims arising from the use of the Service, including but not limited to losses from trading decisions made based on the analysis provided. You use the Service at your own risk.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>8. Modifications</h2>
                <p>TradeNalysis reserves the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>9. Contact</h2>
                <p>For questions regarding these Terms, please contact us at support@tradenalysis.com.</p>
            </div>
        </PolicyLayout>
    );
}

export function PrivacyPage() {
    return (
        <PolicyLayout title="Privacy Policy">
            <p><strong>Effective Date:</strong> January 1, 2026</p>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>1. Information We Collect</h2>
                <p>We collect your name, email address, and payment information when you create an account and subscribe. We also collect usage data including which instruments you analyze and your interaction with the Service.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>2. How We Use Your Information</h2>
                <p>Your information is used to provide and improve the Service, process payments, send account-related communications, and analyze usage patterns to enhance the platform.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>3. Payment Processing</h2>
                <p>Payments are processed by Stripe, a PCI-compliant payment processor. We do not store your full credit card information on our servers. Stripe's privacy policy governs their handling of your payment data.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>4. Data Security</h2>
                <p>We implement industry-standard security measures including encrypted connections (HTTPS), secure password hashing, and JWT-based authentication to protect your data.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>5. Third-Party Services</h2>
                <p>We use third-party services including Stripe (payments), OpenAI (AI analysis), Alpaca (market data), and CoinGecko (cryptocurrency data). These services have their own privacy policies that govern their use of data.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>6. Data Retention</h2>
                <p>We retain your account data and analysis history for as long as your account is active. You may request deletion of your account by contacting support@tradenalysis.com.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>7. Contact</h2>
                <p>For privacy-related questions, contact us at support@tradenalysis.com.</p>
            </div>
        </PolicyLayout>
    );
}

export function RefundPage() {
    return (
        <PolicyLayout title="Refund Policy">
            <p><strong>Effective Date:</strong> January 1, 2026</p>

            <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>No Refund Policy</h2>
                <p className="text-zinc-300"><strong>All payments made to TradeNalysis are final and non-refundable.</strong></p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Free Trial</h2>
                <p>TradeNalysis offers a <strong>3-day free trial</strong> that provides full access to all features. This trial period is designed for you to evaluate the Service before committing to a paid subscription. We strongly encourage you to make full use of the trial to determine whether the Service meets your needs.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Subscription Cancellation</h2>
                <p>You may cancel your subscription at any time through your account settings. Upon cancellation:</p>
                <ul className="list-disc list-inside space-y-1 mt-2 text-zinc-400">
                    <li>You will retain access to the Service until the end of your current billing period</li>
                    <li>No further charges will be made to your payment method</li>
                    <li>No partial or prorated refunds will be issued for the remaining time in your billing period</li>
                </ul>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Chargebacks</h2>
                <p>Filing a chargeback or payment dispute instead of contacting us directly may result in immediate termination of your account and access to the Service.</p>
            </div>

            <div>
                <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Contact</h2>
                <p>If you have questions about billing or your subscription, contact us at support@tradenalysis.com before taking any other action.</p>
            </div>
        </PolicyLayout>
    );
}
